/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 07/08/2025 - 13:10:06
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 07/08/2025
    * - Author          : 
    * - Modification    : 
**/

import React, { useEffect, useState } from "react";
import { Image, Button, Dropdown, Modal, Alert, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import images from "../../../images/index";
import { Link } from "react-router-dom";
import { GoArrowLeft } from "react-icons/go";
import { BsGear } from "react-icons/bs";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { db } from "../../../firebase";
import { doc, getDoc, increment, updateDoc, setDoc } from "firebase/firestore";
import { useParams, useLocation , useNavigate} from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import {
  clearAllPolygonsAndDetectionPoints,
  setSimulationParameter,
  resetAllProjectData
} from "../../../store/reducers/bmapSlice";
import { IoClose } from "react-icons/io5";
import { toast } from "react-toastify";
import { MdSaveAlt } from "react-icons/md";
import { FirebaseError } from "firebase/app";
import { RootState } from "../../../store";
import { Loader_2 } from "../../../components/shared/loader";
import UTCModal from "./UTCModal";
import IconWrapper from "../../../utils/IconWrapper";
import { useTranslation } from 'react-i18next';
import LanguageSelector from "../../../components/shared/LanguageSelector";
// Define interface for simulation parameters
interface SimulationParameters {
  grid_width: number;
  resolution: number;
  sun_elevation_threshold: number;
  beam_spread: number;
  sun_angle: number;
  sun_reflection_threshold: number;
  intensity_threshold: number;
  moduleType?: number | string;
}

// Helper function to save simulation parameters to Firebase
const saveSimulationParameters = async (projectId: string, parameters: SimulationParameters) => {
  try {
    // console.log(`Saving simulation parameters for project ${projectId}:`, parameters);
    
    // Create a reference to the project document
    const projectRef = doc(db, "projects", projectId);
    
    // Check if project document exists
    const projectDoc = await getDoc(projectRef);
    
    if (projectDoc.exists()) {
      // Update existing document with simulation parameters
      await updateDoc(projectRef, {
        simulationParameters: parameters
      });
      // console.log("Successfully saved simulation parameters to database");
    } else {
      // Create new document if it doesn't exist
      await setDoc(projectRef, {
        simulationParameters: parameters
      }, { merge: true });
      // console.log("Created new document with simulation parameters");
    }
    
    return true;
  } catch (error) {
    console.error("Error saving simulation parameters:", error);
    return false;
  }
};

// Helper function to load simulation parameters from Firebase
const loadSimulationParameters = async (projectId: string): Promise<SimulationParameters | null> => {
  try {
    // console.log(`Loading simulation parameters for project ${projectId}`);
    
    // Create a reference to the project document
    const projectRef = doc(db, "projects", projectId);
    
    // Get the document
    const projectDoc = await getDoc(projectRef);
    
    if (projectDoc.exists() && projectDoc.data().simulationParameters) {
      // console.log("Loaded simulation parameters:", projectDoc.data().simulationParameters);
      return projectDoc.data().simulationParameters as SimulationParameters;
    }
    
    // console.log("No simulation parameters found in database");
    return null;
  } catch (error) {
    console.error("Error loading simulation parameters:", error);
    return null;
  }
};

const MapPageHeader: React.FC = () => {
  // datof project thata store in the FirebaseError

  const tiltedPolygons = useSelector(
    (state: RootState) => state.bmap.tiltedPolygons
  );
  const verticalPolygon = useSelector(
    (state: RootState) => state.bmap.verticalPolygons
  );
  const polygonUpperHeight = useSelector(
    (state: RootState) => state.bmap.tiltedupperHeights
  );
  const polygonLowerHeight = useSelector(
    (state: RootState) => state.bmap.tiltedlowerHeights
  );
  const detectionPoint = useSelector(
    (state: RootState) => state.bmap.detectionPoints
  );
  const detectionPointHeight = useSelector(
    (state: RootState) => state.bmap.detectionPointsHeight
  );
  const verticalSlope = useSelector(
    (state: RootState) => state.bmap.verticalSlopeArea
  );
  const verticalLowerHeight = useSelector(
    (state: RootState) => state.bmap.verticalLowerHeight
  );
  const verticalUpperHeight = useSelector(
    (state: RootState) => state.bmap.verticalUpperHeight
  );
  const tiltedCheckedElevation = useSelector(
    (state: RootState) => state.bmap.tiltedElevationChecked
  );
  const tiltedCheckedAzimuth = useSelector(
    (state: RootState) => state.bmap.tiltedAzimuthChecked
  );
  const tiltedCheckedIndex = useSelector(
    (state: RootState) => state.bmap.tiltedEdgeChecked
  );
  const verticalCheckedAzimuth = useSelector(
    (state: RootState) => state.bmap.verticalAzimuthChecked
  );
  const verticalCheckedElevation = useSelector(
    (state: RootState) => state.bmap.verticalElevationChecked
  );
  const verticalSwap = useSelector(
    (state: RootState) => state.bmap.verticalSwap
  );
  const tiltedSwap = useSelector((state: RootState) => state.bmap.tiltedSwap);

  const excludeArea = useSelector((state: RootState) => state.bmap.excludeArea);
  const Slope = useSelector((state: RootState) => state.bmap.slopeArea);
  const rulerLine = useSelector((state: RootState) => state.bmap.rulerLines);
  const center = useSelector((state: RootState) => state.bmap.center);
  // --------------------
  const [SmShow, setSmShow] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [changeValue, setChangeValue] = useState<boolean>(true);
  const [helpTooltip, setHelpTooltip] = useState<boolean>();
  const [UTCModalShow, setUTCModalShow] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { t } = useTranslation(['mapPage', 'common']);
  const navigate = useNavigate();

  useEffect(() => {
    // Show the highlight for 5 seconds when the page loads
    setHelpTooltip(true);
    const timer = setTimeout(() => {
      setHelpTooltip(false);
    }, 5000);
    return () => clearTimeout(timer); // Cleanup the timer
  }, []);
  
  const [simulationError, setSimulationError] = useState({
    gridWidth: "",
    resolution: "",
    sun_elevation_threshold: "",
    beam_spread: "",
    sun_angle: "",
    sun_reflection_threshold: "",
    intensity_threshold: "",
  });
  const dispatch = useDispatch();
  const simulaterParameter = useSelector(
    (state: RootState) => state.bmap.simulationParameter
  );

  // --------------fetch project name-----
  const [projectInformation, setProjectInformation] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string | any }>();
  
  // Load project information and simulation parameters
  useEffect(() => {
    const getDocument = async () => {
      if (!projectId) return;
      
      setLoading(true);
      try {
        const docRef = doc(db, "projects", projectId);
        const docSnap = await getDoc(docRef);
  
        if (docSnap.exists()) {
          setProjectInformation(docSnap.data());
          localStorage.setItem("projectName", docSnap.data().projectName);
          
          // Load simulation parameters
          const parameters = await loadSimulationParameters(projectId);
          if (parameters) {
            // Update Redux store with loaded parameters
            Object.entries(parameters).forEach(([key, value]) => {
              dispatch(setSimulationParameter({ key, value }));
            });
          } else {
            // NEW CODE: If no parameters exist (new project), set default values
            const defaultValues = {
              grid_width: 1,
              resolution: 5,
              sun_elevation_threshold: 5,
              beam_spread: 5,
              sun_angle: 0.5,
              sun_reflection_threshold: 10,
              intensity_threshold: 30000,
              moduleType: 1  // Default to Standard PV module with ARC
            };
            
            // Update Redux store with defaults
            Object.entries(defaultValues).forEach(([key, value]) => {
              dispatch(setSimulationParameter({ key, value }));
            });
            
            // Save defaults to Firebase
            await saveSimulationParameters(projectId, defaultValues);
            // console.log("Default values set for new project");
          }
        } else {
          // console.log("No such document!");
        }
      } catch (error) {
        console.error("Error loading project data:", error);
        toast.error("Error loading project data");
      } finally {
        setLoading(false);
      }
    };
    
    getDocument();
  }, [dispatch, projectId]);

  // Save parameters to Firebase when they change
  useEffect(() => {
    // Create debounce function to avoid too many writes
    const debounceSave = setTimeout(() => {
      if (projectId && !hasErrors && !loading) {
        saveSimulationParameters(projectId, simulaterParameter);
      }
    }, 500); // Wait 500ms after changes before saving
    
    return () => clearTimeout(debounceSave);
  }, [simulaterParameter, projectId, hasErrors, loading]);

  const changeSimulationValue = async (key:any, value:any) => {
    if (key === "moduleType") {
      // For moduleType, ensure the value is stored as a number
      const moduleTypeValue = parseInt(value, 10);
      dispatch(setSimulationParameter({ key, value: moduleTypeValue }));
    } else {
      // For other parameters, handle as before
      const convertedValue = typeof value === 'string' ? value.replace(",", ".") : value;
      if (!isNaN(convertedValue) || value === "") {
        dispatch(setSimulationParameter({ key, value: convertedValue }));
      }
    }
  };

  useEffect(() => {
    const errors = {
      gridWidth: "",
      resolution: "",
      sun_elevation_threshold: "",
      beam_spread: "",
      sun_angle: "",
      sun_reflection_threshold: "",
      intensity_threshold: "",
    };

    let hasErrors = false;

    if (
      simulaterParameter.grid_width < 0.1 ||
      simulaterParameter.grid_width > 3
    ) {
      errors.gridWidth = "Grid width is between 0.1 to 3";
      hasErrors = true;
    }
    if (
      simulaterParameter.intensity_threshold < 0 ||
      simulaterParameter.intensity_threshold == Number(" ")
    ) {
      errors.intensity_threshold = "Intensity threshold value is required";
      hasErrors = true;
    }
    if (
      simulaterParameter.resolution < 1 ||
      simulaterParameter.resolution > 15
    ) {
      errors.resolution = "Resolution value is between 1 to 15";
      hasErrors = true;
    }

    if (
      simulaterParameter.sun_elevation_threshold < 0 ||
      simulaterParameter.sun_elevation_threshold > 90 ||
      simulaterParameter.sun_elevation_threshold == Number(" ")
    ) {
      errors.sun_elevation_threshold =
        "Sun elevation threshold value is between 0 to 90";
      hasErrors = true;
    }

    if (
      simulaterParameter.beam_spread < 0 ||
      simulaterParameter.beam_spread > 30 ||
      simulaterParameter.beam_spread == Number(" ")
    ) {
      errors.beam_spread = "Beam spread value is between 0 to 30";
      hasErrors = true;
    }

    if (
      simulaterParameter.sun_angle < 0.1 ||
      simulaterParameter.sun_angle > 1
    ) {
      errors.sun_angle = "Apparent diameter of sun is between 0.1 to 1";
      hasErrors = true;
    }

    if (
      simulaterParameter.sun_reflection_threshold < 0 ||
      simulaterParameter.sun_reflection_threshold > 360 ||
      simulaterParameter.sun_reflection_threshold == Number(" ")
    ) {
      errors.sun_reflection_threshold =
        "Threshold for difference angle value is between 0 to 360";
      hasErrors = true;
    }

    setSimulationError(errors);
    setHasErrors(hasErrors);
  }, [simulaterParameter]);

  const closeModal = async () => {
    if (!hasErrors) {
      // Force save parameters before closing the modal
      if (projectId) {
        setIsSaving(true);
        try {
          const success = await saveSimulationParameters(projectId, simulaterParameter);
          if (!success) {
            toast.error("Failed to save simulation parameters");
          }
        } catch (error) {
          console.error("Error saving simulation parameters:", error);
          toast.error("Error saving simulation parameters");
        } finally {
          setIsSaving(false);
        }
      }
      setSmShow(false);
    } else {
      toast.error("Invalid simulation parameter value");
    }
  };

  const toogleUTCModal = () => {
    setUTCModalShow(!UTCModalShow);
  };
  
  const tooltip = (
    <Tooltip id="tooltip" style={{marginTop:"-30px",marginRight:"-90px"}}> <small> {t('mapPage:tooltip.helpAvailable')} </small> </Tooltip>
  );
  
  const handleDefaultValue = async () => {
    // Set default values
    const defaultValues = {
      grid_width: 1,
      resolution: 5,
      sun_elevation_threshold: 5,
      beam_spread: 5,
      sun_angle: 0.5,
      sun_reflection_threshold: 10,
      intensity_threshold: 30000,
      moduleType: 1  // Default to Standard PV module (value 1)
    };
    
    // Update Redux state with defaults
    Object.entries(defaultValues).forEach(([key, value]) => {
      dispatch(setSimulationParameter({ key, value }));
    });
    
    // Save defaults to Firebase
    if (projectId) {
      setIsSaving(true);
      try {
        await saveSimulationParameters(projectId, defaultValues);
        toast.success("Default values set");
      } catch (error) {
        console.error("Error saving default values:", error);
        toast.error("Error saving default values");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleBackToDashboard = () => {
    // Clear Redux state before navigating to a new project
    dispatch(resetAllProjectData());
    
    // Then navigate to the project
    navigate(`/dashboard`);
  };

  return (
    <div className="mapPageHeader">
      <Link to={"https://pv-glarecheck.com/"}>
        <Image
          className="logo"
          src={images.Logo}
          alt="logoHere"
          width={194}
          height={36}
        />
      </Link>
      <div className="centerpart">
      <div 
          className="dashboard" 
          onClick={handleBackToDashboard}
          style={{ cursor: 'pointer' }}
        >
          <IconWrapper Icon={GoArrowLeft} />
          {t('common:navigation.dashboard')}
        </div>
      <p className="m-0">
        {t('mapPage:header.activeProject')} / {projectInformation?.projectName}
      </p>
      </div>
      <div className="headerButton">
        <Dropdown>
          <Dropdown.Toggle
            variant="none"
            className="optionDropdown"
            id="dropdown-basic"
          >
            <Button className="option" variant="none">
              {t('mapPage:buttons.options')} <IconWrapper Icon={BsGear} style={{ marginTop: "2px" }} size={15} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu variant="none" style={{ width: "200px" }}>
            <Dropdown.Item
              onClick={() => setSmShow(true)}
              className="optionReload"
            >
              {t('mapPage:dropdownOptions.simulationParameter')}
            </Dropdown.Item>
            <Dropdown.Item onClick={toogleUTCModal} className="optionReload">
              {t('mapPage:dropdownOptions.changeUTC')}
            </Dropdown.Item>
            <Dropdown.Item
              className="optionReload"
              onClick={() => {
                dispatch(clearAllPolygonsAndDetectionPoints());
              }}
            >
              {t('mapPage:dropdownOptions.removeAllObjects')}
            </Dropdown.Item>
            
            <div className="optionReload">
              Language <LanguageSelector />
              
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <OverlayTrigger show={helpTooltip} placement="left" overlay={tooltip}>
          <Button
            href="https://pv-glarecheck.com/help"
            className="help"
            variant="none"
            target="_blank"
          >
            {t('mapPage:buttons.help')}
            <IconWrapper Icon={IoIosInformationCircleOutline} style={{ marginTop: "2px" }} size={20} />
          </Button>
        </OverlayTrigger>
      </div>
      <Modal
        className="mapPageHeader"
        show={SmShow}
        aria-labelledby="example-modal-sizes-title-sm"
      >
        <Modal.Header className="d-flex justify-content-between align-items-center">
          <Modal.Title className="" id="example-modal-sizes-title-sm">
            {t('mapPage:modals.simulation.title')}
          </Modal.Title>
          <IconWrapper Icon={IoClose}  onClick={closeModal}
            size={25}
            className="mt-1"
            cursor={"pointer"} />
        </Modal.Header>
        <Modal.Body
          style={{ overflowY: "auto", height: "550px", margin: "auto" }}
        >
          <Alert variant={"warning"} style={{ fontSize: "14px" }}>
            {t('mapPage:modals.simulation.warning')}
            <a
              target="_block"
              href="https://pv-glarecheck.com/"
              className="text-decoration-none"
              style={{ cursor: "pointer" }}
            >
              {t('mapPage:modals.simulation.helpLink')}
            </a>{" "}
            {t('mapPage:modals.simulation.moreInfo')}
          </Alert>
          
          {isSaving && (
            <div className="text-center my-2">
              <Loader_2 /> Saving parameters...
            </div>
          )}
          
          <p className="error text-center m-0" style={{ fontSize: "12px" }}>
            {simulationError.gridWidth}
          </p>
          <div className="coolinput">
            <label htmlFor="input" className="text">
              Resolution [min] :
            </label>
            <input
              type="text"
              name="resolution"
              className={`input ${changeValue ? "disabledInput" : ""} `}
              disabled={changeValue}
              onChange={(e) => {
                changeSimulationValue("resolution", e.target.value);
              }}
              value={simulaterParameter.resolution}
            />
          </div>
          <p className="error text-center m-0" style={{ fontSize: "12px" }}>
            {simulationError.resolution}
          </p>
          <div className="coolinput">
            <label htmlFor="input" className="text">
              Sun elevation threshold [&deg;] :
            </label>
            <input
              type="text"
              name="SunElevationThreshold"
              className={`input ${changeValue ? "disabledInput" : ""} `}
              disabled={changeValue}
              onChange={(e) => {
                changeSimulationValue(
                  "sun_elevation_threshold",
                  e.target.value
                );
              }}
              value={simulaterParameter.sun_elevation_threshold}
            />
          </div>
          <p className="error text-center m-0" style={{ fontSize: "12px" }}>
            {simulationError.sun_elevation_threshold}
          </p>
          <div className="coolinput">
            <label htmlFor="input" className="text">
              Beam Spread [&deg;] :
            </label>
            <input
              type="text"
              name="beamSpread"
              className={`input ${changeValue ? "disabledInput" : ""} `}
              disabled={changeValue}
              onChange={(e) => {
                changeSimulationValue("beam_spread", e.target.value);
              }}
              value={simulaterParameter.beam_spread}
            />
          </div>
          <p className="error text-center m-0" style={{ fontSize: "12px" }}>
            {simulationError.beam_spread}
          </p>
          <div className="coolinput">
            <label htmlFor="input" className="text">
              Apparent diameter of the sun [&deg;] :
            </label>
            <input
              type="text"
              name="sunAngle"
              className={`input ${changeValue ? "disabledInput" : ""} `}
              disabled={changeValue}
              onChange={(e) => {
                changeSimulationValue("sun_angle", e.target.value);
              }}
              value={simulaterParameter.sun_angle}
            />
          </div>
          <p className="error text-center m-0" style={{ fontSize: "12px" }}>
            {simulationError.sun_angle}
          </p>
          <div className="coolinput">
            <label htmlFor="input" className="text">
              Threshold for the different angle [&deg;] :
            </label>
            <input
              type="text"
              name="sunReflectionThreshold"
              className={`input ${changeValue ? "disabledInput" : ""} `}
              disabled={changeValue}
              onChange={(e) => {
                changeSimulationValue(
                  "sun_reflection_threshold",
                  e.target.value
                );
              }}
              value={simulaterParameter.sun_reflection_threshold}
            />
          </div>
          <p className="error m-0 text-center" style={{ fontSize: "12px" }}>
            {simulationError.sun_reflection_threshold}
          </p>
          <div className="coolinput">
            <label htmlFor="input" className="text">
              Intensity threshold :
            </label>
            <input
              type="text"
              name="intensityThreshold"
              className={`input ${changeValue ? "disabledInput" : ""} `}
              disabled={changeValue}
              onChange={(e) => {
                changeSimulationValue("intensity_threshold", e.target.value);
              }}
              value={simulaterParameter.intensity_threshold}
            />
          </div>
          <p className="error m-0 text-center" style={{ fontSize: "12px" }}>
            {simulationError.intensity_threshold}
          </p>
          {/* --------------------------- */}
          <div className="coolinput">
            <label htmlFor="input" className="text">
              Module type:
            </label>
            <Form.Select
              aria-label="Module type selection"
              style={{ height: "100%", outline: "none", boxShadow: "none" }}
              className={`input mb-3 ${changeValue ? "disabledInput" : ""}`}
              disabled={changeValue}
              onChange={(e) => {
                // Ensure the value is stored as a number
                const moduleTypeValue = parseInt(e.target.value, 10);
                changeSimulationValue("moduleType", moduleTypeValue);
              }}
              // Improved value handling to ensure default is properly shown
              value={simulaterParameter.moduleType !== undefined ? 
                    String(simulaterParameter.moduleType) : "1"}
            >
              <option value="1">Standard PV module with ARC</option>
              <option value="2">Phytonics Anti Glare Coating</option>
              <option value="3">Megasol ZRM+ Glare Reduced Module</option>
            </Form.Select>
          </div>

<div className="d-flex flex-column justify-content-center align-items-center my-3">
  <div className="d-flex justify-content-center align-items-center gap-2 w-100">
    <Button
      onClick={() => setChangeValue(!changeValue)}  // Toggle locked/unlocked state
      variant="none"
      className={`ChangeValuesBtn ${!changeValue ? 'active' : ''}`}
      disabled={isSaving}
    >
      {!changeValue ? 'Lock Fields' : 'Unlock Fields'}
      {!changeValue && <span className="ms-1">✓</span>}
    </Button>
    <Button
      onClick={() => {
        setChangeValue(true);
        handleDefaultValue();
      }}
      variant="none"
      className="SetDeafultValueBtn"
      disabled={isSaving}
    >
      Set default values
    </Button>
  </div>
  
  {/* Status indicator - only shown when fields are unlocked */}
  {!changeValue && (
    <div className="text-center mt-2">
      <small className="text-success fw-bold">
        Fields are now editable
      </small>
    </div>
  )}
</div>
        </Modal.Body>
      </Modal>
      <UTCModal show={UTCModalShow} projectID={projectId} closeModal={toogleUTCModal} />
    </div>
  );
};

export default MapPageHeader;